import { doc, setDoc, getDoc, collection, query, where, orderBy, limit, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';

export interface UserInteraction {
  userId: string;
  contentId: number;
  type: 'movie' | 'tv';
  action: 'view' | 'complete' | 'skip' | 'like' | 'dislike' | 'rate';
  timestamp: Date;
  watchDuration?: number; // in seconds
  completionRate?: number; // 0-1
  language?: string;
  genres?: number[];
  rating?: number; // 1-10
  sessionId?: string;
  deviceType?: string;
  timeOfDay?: 'morning' | 'afternoon' | 'evening' | 'night';
}

export interface UserPreferences {
  userId: string;
  languages: string[];
  preferredGenres: number[];
  dislikedGenres: number[];
  preferredActors: number[];
  preferredDirectors: number[];
  averageSessionLength: number;
  preferredTimeOfDay: string[];
  lastUpdated: Date;
}

export interface ContentEngagement {
  contentId: number;
  type: 'movie' | 'tv';
  totalViews: number;
  completionRate: number;
  averageRating: number;
  skipRate: number;
  recommendationScore: number;
}

class UserAnalytics {
  private interactionCache: Map<string, UserInteraction[]> = new Map();
  private preferencesCache: Map<string, UserPreferences> = new Map();

  async trackInteraction(interaction: UserInteraction): Promise<void> {
    try {
      // Add time of day
      interaction.timeOfDay = this.getTimeOfDay();

      // Generate session ID if not provided
      if (!interaction.sessionId) {
        interaction.sessionId = this.getCurrentSessionId();
      }

      // Save to Firestore
      const docRef = doc(collection(db, 'userInteractions'));
      await setDoc(docRef, {
        ...interaction,
        timestamp: serverTimestamp(),
      });

      // Update local cache
      const userId = interaction.userId;
      const cached = this.interactionCache.get(userId) || [];
      cached.push(interaction);
      this.interactionCache.set(userId, cached);

      // Update user preferences based on interaction
      await this.updateUserPreferences(interaction);
    } catch (error) {
    }
  }

  async getUserInteractions(
    userId: string,
    limitCount: number = 100
  ): Promise<UserInteraction[]> {
    // Check cache first
    if (this.interactionCache.has(userId)) {
      return this.interactionCache.get(userId)!.slice(-limitCount);
    }

    try {
      const q = query(
        collection(db, 'userInteractions'),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );

      const snapshot = await getDocs(q);
      const interactions: UserInteraction[] = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        interactions.push({
          ...data,
          timestamp: data.timestamp?.toDate() || new Date(),
        } as UserInteraction);
      });

      // Cache the results
      this.interactionCache.set(userId, interactions);

      return interactions;
    } catch (error) {
      return [];
    }
  }

  async getUserLanguagePreferences(userId: string): Promise<string[]> {
    const interactions = await this.getUserInteractions(userId);

    // Count language occurrences
    const languageCounts = new Map<string, number>();
    interactions.forEach(interaction => {
      if (interaction.language) {
        const count = languageCounts.get(interaction.language) || 0;
        languageCounts.set(interaction.language, count + 1);
      }
    });

    // Sort by frequency and return top languages
    const sortedLanguages = Array.from(languageCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([lang]) => lang);

    // If no history, return default based on browser or region
    if (sortedLanguages.length === 0) {
      return this.getDefaultLanguages();
    }

    // Return primary languages (those with significant usage)
    const threshold = interactions.length * 0.1; // At least 10% of content
    return sortedLanguages.filter(lang => {
      const count = languageCounts.get(lang) || 0;
      return count >= threshold;
    });
  }

  private getDefaultLanguages(): string[] {
    // Get browser language
    const browserLang = navigator.language.split('-')[0];

    // Common defaults based on region
    const defaults = ['en']; // English as fallback

    if (browserLang && browserLang !== 'en') {
      defaults.unshift(browserLang);
    }

    return defaults;
  }

  async updateUserPreferences(interaction: UserInteraction): Promise<void> {
    const userId = interaction.userId;
    const prefs = await this.getUserPreferences(userId);

    // Update language preferences
    if (interaction.language) {
      if (!prefs.languages.includes(interaction.language)) {
        prefs.languages.push(interaction.language);
      }
    }

    // Update genre preferences
    if (interaction.genres && interaction.action === 'complete') {
      interaction.genres.forEach(genreId => {
        if (!prefs.preferredGenres.includes(genreId)) {
          prefs.preferredGenres.push(genreId);
        }
      });
    }

    // Update disliked genres
    if (interaction.genres && interaction.action === 'skip') {
      interaction.genres.forEach(genreId => {
        if (!prefs.dislikedGenres.includes(genreId)) {
          prefs.dislikedGenres.push(genreId);
        }
      });
    }

    // Update time of day preferences
    if (interaction.timeOfDay) {
      if (!prefs.preferredTimeOfDay.includes(interaction.timeOfDay)) {
        prefs.preferredTimeOfDay.push(interaction.timeOfDay);
      }
    }

    // Save updated preferences
    await this.saveUserPreferences(prefs);
  }

  async getUserPreferences(userId: string): Promise<UserPreferences> {
    // Check cache
    if (this.preferencesCache.has(userId)) {
      return this.preferencesCache.get(userId)!;
    }

    try {
      const docRef = doc(db, 'userPreferences', userId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const prefs = {
          ...data,
          lastUpdated: data.lastUpdated?.toDate() || new Date(),
        } as UserPreferences;

        this.preferencesCache.set(userId, prefs);
        return prefs;
      }
    } catch (error) {
    }

    // Return default preferences
    const defaultPrefs: UserPreferences = {
      userId,
      languages: this.getDefaultLanguages(),
      preferredGenres: [],
      dislikedGenres: [],
      preferredActors: [],
      preferredDirectors: [],
      averageSessionLength: 0,
      preferredTimeOfDay: [],
      lastUpdated: new Date(),
    };

    this.preferencesCache.set(userId, defaultPrefs);
    return defaultPrefs;
  }

  private async saveUserPreferences(prefs: UserPreferences): Promise<void> {
    try {
      const docRef = doc(db, 'userPreferences', prefs.userId);
      await setDoc(docRef, {
        ...prefs,
        lastUpdated: serverTimestamp(),
      });

      // Update cache
      this.preferencesCache.set(prefs.userId, prefs);
    } catch (error) {
    }
  }

  async getContentEngagement(contentId: number, type: 'movie' | 'tv'): Promise<ContentEngagement> {
    try {
      const q = query(
        collection(db, 'userInteractions'),
        where('contentId', '==', contentId),
        where('type', '==', type)
      );

      const snapshot = await getDocs(q);

      let totalViews = 0;
      let totalCompletion = 0;
      let totalRating = 0;
      let ratingCount = 0;
      let skipCount = 0;

      snapshot.forEach(doc => {
        const data = doc.data() as UserInteraction;

        if (data.action === 'view') totalViews++;
        if (data.action === 'skip') skipCount++;
        if (data.completionRate) totalCompletion += data.completionRate;
        if (data.rating) {
          totalRating += data.rating;
          ratingCount++;
        }
      });

      const engagementCount = snapshot.size;

      return {
        contentId,
        type,
        totalViews,
        completionRate: engagementCount > 0 ? totalCompletion / engagementCount : 0,
        averageRating: ratingCount > 0 ? totalRating / ratingCount : 0,
        skipRate: totalViews > 0 ? skipCount / totalViews : 0,
        recommendationScore: this.calculateRecommendationScore({
          completionRate: totalCompletion / Math.max(engagementCount, 1),
          averageRating: ratingCount > 0 ? totalRating / ratingCount : 0,
          skipRate: totalViews > 0 ? skipCount / totalViews : 0,
          viewCount: totalViews,
        }),
      };
    } catch (error) {
      return {
        contentId,
        type,
        totalViews: 0,
        completionRate: 0,
        averageRating: 0,
        skipRate: 0,
        recommendationScore: 0.5,
      };
    }
  }

  private calculateRecommendationScore(metrics: {
    completionRate: number;
    averageRating: number;
    skipRate: number;
    viewCount: number;
  }): number {
    // Weight different metrics
    const completionWeight = 0.3;
    const ratingWeight = 0.4;
    const skipWeight = -0.2;
    const popularityWeight = 0.1;

    let score = 0;

    // Completion rate (0-1)
    score += metrics.completionRate * completionWeight;

    // Rating (normalize to 0-1)
    score += (metrics.averageRating / 10) * ratingWeight;

    // Skip rate (negative factor)
    score += (1 - metrics.skipRate) * Math.abs(skipWeight);

    // Popularity (log scale)
    const popularityScore = Math.min(Math.log10(metrics.viewCount + 1) / 3, 1);
    score += popularityScore * popularityWeight;

    return Math.max(0, Math.min(1, score));
  }

  private getTimeOfDay(): 'morning' | 'afternoon' | 'evening' | 'night' {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return 'morning';
    if (hour >= 12 && hour < 17) return 'afternoon';
    if (hour >= 17 && hour < 21) return 'evening';
    return 'night';
  }

  private getCurrentSessionId(): string {
    // Use session storage to maintain session ID
    let sessionId = sessionStorage.getItem('analytics_session_id');

    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('analytics_session_id', sessionId);
    }

    return sessionId;
  }

  async trackView(
    userId: string,
    contentId: number,
    type: 'movie' | 'tv',
    language?: string,
    genres?: number[]
  ): Promise<void> {
    await this.trackInteraction({
      userId,
      contentId,
      type,
      action: 'view',
      timestamp: new Date(),
      language,
      genres,
    });
  }

  async trackCompletion(
    userId: string,
    contentId: number,
    type: 'movie' | 'tv',
    watchDuration: number,
    completionRate: number
  ): Promise<void> {
    await this.trackInteraction({
      userId,
      contentId,
      type,
      action: 'complete',
      timestamp: new Date(),
      watchDuration,
      completionRate,
    });
  }

  async trackSkip(
    userId: string,
    contentId: number,
    type: 'movie' | 'tv',
    watchDuration: number
  ): Promise<void> {
    await this.trackInteraction({
      userId,
      contentId,
      type,
      action: 'skip',
      timestamp: new Date(),
      watchDuration,
      completionRate: 0,
    });
  }

  async trackRating(
    userId: string,
    contentId: number,
    type: 'movie' | 'tv',
    rating: number
  ): Promise<void> {
    await this.trackInteraction({
      userId,
      contentId,
      type,
      action: 'rate',
      timestamp: new Date(),
      rating,
    });
  }

  clearCache(userId?: string): void {
    if (userId) {
      this.interactionCache.delete(userId);
      this.preferencesCache.delete(userId);
    } else {
      this.interactionCache.clear();
      this.preferencesCache.clear();
    }
  }
}

// Singleton instance
const analytics = new UserAnalytics();

export const trackView = analytics.trackView.bind(analytics);
export const trackCompletion = analytics.trackCompletion.bind(analytics);
export const trackSkip = analytics.trackSkip.bind(analytics);
export const trackRating = analytics.trackRating.bind(analytics);
export const trackInteraction = analytics.trackInteraction.bind(analytics);
export const getUserInteractions = analytics.getUserInteractions.bind(analytics);
export const getUserLanguagePreferences = analytics.getUserLanguagePreferences.bind(analytics);
export const getUserPreferences = analytics.getUserPreferences.bind(analytics);
export const getContentEngagement = analytics.getContentEngagement.bind(analytics);
export const clearAnalyticsCache = analytics.clearCache.bind(analytics);

export default analytics;